import React, {useEffect, useCallback} from 'react';
import {inject, observer} from 'mobx-react';
import {MyLibraryModal} from '../../MyLibraryModal';
import MyLibraryModalStore from '../../MyLibraryModal/store/MyLibraryModalStore';
import {
  MediaFile,
  MEDIA_FILES_ALL,
  MEDIA_FILES_ALL_VIDEO,
  MEDIA_FILES_AUDIO,
  MEDIA_FILES_IMAGE,
  MEDIA_FILES_VIDEO,
} from '../../../data/models/LibraryFile/MediaFile';

type Mode = 'videos' | 'images' | 'media' | 'audio';

interface Props {
  organizationId?: string;
  onCancel: () => void;
  onSelect: (file: MediaFile) => boolean | void | Promise<boolean | void>;
  myLibraryModalStore?: MyLibraryModalStore;
  mode?: Mode;
  minDurationSeconds?: number;
}

const BrowseMediaLibraryContainer = inject('myLibraryModalStore')(
  observer(
    ({
      organizationId,
      onCancel,
      onSelect,
      myLibraryModalStore,
      mode = 'videos',
      minDurationSeconds,
    }: Props) => {
      const isValidFileForMode = (f: MediaFile) => {
        const t = (f?.fileType || '').toLowerCase();
        if (mode === 'audio') return t.includes('audio');
        return t.includes('image') || t.includes('video');
      };

      const handleConfirm = useCallback(
        async (files: MediaFile | MediaFile[]) => {
          const picked: MediaFile = Array.isArray(files) ? files[0] : files;
          if (!picked || !isValidFileForMode(picked)) {
            alert(
              mode === 'audio'
                ? 'Please select an audio file.'
                : 'Please select an image or video file.',
            );
            return false;
          }
          const result = onSelect(picked);
          const resolved = await Promise.resolve(result);
          const shouldClose = resolved !== false;
          if (shouldClose) {
            const s: any = myLibraryModalStore;
            if (s) s.isLibraryShown = false;
          }
          return shouldClose;
        },
        [onSelect, myLibraryModalStore],
      );

      const handleCancel = useCallback(() => {
        try {
          onCancel();
        } finally {
          const s: any = myLibraryModalStore;
          if (s) s.isLibraryShown = false;
        }
      }, [onCancel, myLibraryModalStore]);

      useEffect(() => {
        const s: any = myLibraryModalStore;
        if (!s) return;

        if (!s.organizationId && organizationId) {
          s.organizationId = organizationId;
        }

        s.onFileCallback = handleConfirm;
        if (typeof s.setOnCancelCallback === 'function') {
          s.setOnCancelCallback(handleCancel);
        } else {
          s.onCancelCallback = handleCancel;
        }

        const type =
          mode === 'images'
            ? MEDIA_FILES_IMAGE
            : mode === 'media'
            ? MEDIA_FILES_ALL
            : mode === 'audio'
            ? MEDIA_FILES_AUDIO
            : MEDIA_FILES_ALL_VIDEO;
        const initialFilter = mode === 'media' ? MEDIA_FILES_VIDEO : type;

        if ('type' in s) s.type = type;
        if ('selectedFilter' in s) s.selectedFilter = initialFilter;
        if ('sourceType' in s) s.sourceType = type;
        if ('setExcludeDocuments' in s) {
          s.setExcludeDocuments(mode === 'media');
        } else {
          s.excludeDocuments = mode === 'media';
        }
        if ('setInitialSelectedFilter' in s) {
          s.setInitialSelectedFilter(initialFilter);
        } else {
          s.initialSelectedFilter = initialFilter;
        }
        if ('setMinDurationSeconds' in s) {
          s.setMinDurationSeconds(minDurationSeconds ?? null);
        } else {
          s.minDurationSeconds = minDurationSeconds ?? null;
        }

        if ('page' in s) s.page = 1;
        if ('search' in s) s.search = '';

        s.isLibraryShown = true;

        if (typeof s.refreshFiles === 'function') {
          s.refreshFiles();
        } else if (typeof s.fetch === 'function') {
          s.fetch();
        }

        return () => {
          s.onFileCallback = undefined;
          if (typeof s.setOnCancelCallback === 'function') {
            s.setOnCancelCallback(undefined);
          } else {
            s.onCancelCallback = undefined;
          }
          if ('setExcludeDocuments' in s) {
            s.setExcludeDocuments(false);
          } else {
            s.excludeDocuments = false;
          }
          if ('setInitialSelectedFilter' in s) {
            s.setInitialSelectedFilter(undefined);
          } else {
            s.initialSelectedFilter = undefined;
          }
          if ('setMinDurationSeconds' in s) {
            s.setMinDurationSeconds(null);
          } else {
            s.minDurationSeconds = null;
          }
          s.isLibraryShown = false;
        };
      }, [
        myLibraryModalStore,
        handleConfirm,
        handleCancel,
        organizationId,
        mode,
        minDurationSeconds,
      ]);

      return <MyLibraryModal />;
    },
  ),
);

export default BrowseMediaLibraryContainer;
